import React, { useCallback, useMemo } from 'react';
import Button from '@app/react-common/components/button/Button';
import { RuleEngineV2 } from '@app/react-common/components/rule-engine-v2/RuleEngineV2';
import { RuleEngineState } from '@app/react-common/components/rule-engine-v2/models/RuleEngineState';
import { Property, RuleEngineConfig } from '@app/react-common/components/rule-engine-v2/models/RuleEngineConfig';
import {
  mapRuleEngineApiDtoToRuleEngineState,
  mapRuleEngineStateToRuleEngineApiDto,
} from '@app/react-common/components/rule-engine-v2/utils/ruleEngineMapper';
import { AbTestFormData } from './abTestModalUtils';
import { AbTest } from '../../../models/AbTest';
import { AbTestWidgetType } from '../../../models/AbTestCreationType';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import { countryCodeToDisplayNameMap } from '@pdqdev/pdq-shared/dist/utils/Country-utils';
import { getKeyByStringValue } from '@utils/string-utils';
import { getButtonText } from './abTestModalUtils';
import { usePdqCoreApi } from '@app/react-common/api/usePdqCoreApi';

interface AbTestModalRulesTabProps {
  formData: AbTestFormData;
  mode: 'create' | 'edit' | 'view' | 'duplicate';
  showInvalidStep: number;
  rulesValid: boolean;
  widgetType: AbTestWidgetType;
  shop: Shop;
  existingAbTest?: AbTest;
  selectedExtensionGroupIds?: string;
  initialRulesFromExtensionGroupsForNewABTest?: RuleEngineState | null;
  filteredRulesBasedOnTestTypeCatagory: RuleEngineConfig;
  checkoutApi: any;
  showToast: (options: { message: string; mode: string }) => void;
  setFormData: React.Dispatch<React.SetStateAction<AbTestFormData>>;
  setRulesValid: (valid: boolean) => void;
  getPreviousTab: (currentTabName: string) => string | null;
  getNextTab: (currentTabName: string) => string | null;
  isNextTabTokenForShopify?: (currentTabName: string) => boolean;
  setActiveTab: (tab: string) => void;
  handleTabChange: (tab: string) => void;
  handleRulesValidation: (nextTab: string) => void;
  handleSave: () => void;
}

export const AbTestModalRulesTab: React.FC<AbTestModalRulesTabProps> = React.memo(
  ({
    formData,
    mode,
    showInvalidStep,
    rulesValid,
    widgetType,
    shop,
    existingAbTest,
    selectedExtensionGroupIds = '',
    initialRulesFromExtensionGroupsForNewABTest = null,
    filteredRulesBasedOnTestTypeCatagory,
    showToast,
    setFormData,
    setRulesValid,
    getPreviousTab,
    getNextTab,
    isNextTabTokenForShopify,
    setActiveTab,
    handleTabChange,
    handleRulesValidation,
    handleSave,
  }) => {
    const autocompleteFailureHandler = useCallback(async () => {
      showToast({ message: 'An error has occurred. Please contact support if the problem persists.', mode: 'danger' });
    }, [showToast]);
    const pdqCoreApi = usePdqCoreApi(autocompleteFailureHandler);

    const rulesTitle = useMemo(() => {
      if (mode == 'view') {
        return formData?.abTestRules?.ruleGroups?.length > 0
          ? '*Run test for checkouts that meet the following criteria:'
          : 'No rules have been set for this test.';
      }
      return '*Run test for checkouts that meet the following criteria:';
    }, [mode, formData?.abTestRules?.ruleGroups?.length]);

    const ruleEngineInitialState = useMemo(() => {
      return formData?.abTestRules
        ? mapRuleEngineApiDtoToRuleEngineState(formData.abTestRules)
        : initialRulesFromExtensionGroupsForNewABTest || {
            matchAllRuleGroups: true,
            ruleGroups: [],
          };
    }, [formData?.abTestRules, initialRulesFromExtensionGroupsForNewABTest]);

    const ruleEngineKey = useMemo(
      () => (existingAbTest ? undefined : `rule-engine-${selectedExtensionGroupIds}`),
      [existingAbTest, selectedExtensionGroupIds],
    );

    const handleRuleEngineChange = useCallback(
      (state: RuleEngineState, valid: boolean) => {
        setFormData((prevData) => ({
          ...prevData,
          abTestRules: mapRuleEngineStateToRuleEngineApiDto(state),
        }));
        setRulesValid(valid);
      },
      [setFormData, setRulesValid],
    );

    const fetchAutocompleteValues = useCallback(
      async (property: Property, input: string = ''): Promise<string[]> => {
        if (property === Property.country) {
          return Object.values(countryCodeToDisplayNameMap);
        }
        const returnObject = await pdqCoreApi.get(
          `/conditions/autocomplete/fetch?input=${encodeURIComponent(input)}&autocompleteBy=${getKeyByStringValue(Property, property)}`,
        );
        return returnObject.data;
      },
      [pdqCoreApi],
    );

    const ruleEngineBindings = useMemo(
      () => ({
        viewMode: mode == 'view',
        showValidationErrors: showInvalidStep == 2,
        hideMatchAllRuleGroupsTab: shop?.metadata?.enablePredictedSegmentAbTestWinner !== false,
        onChange: handleRuleEngineChange,
        fetchAutocompleteValues,
        showToast: showToast,
      }),
      [
        mode,
        showInvalidStep,
        shop?.metadata?.enablePredictedSegmentAbTestWinner,
        handleRuleEngineChange,
        fetchAutocompleteValues,
        showToast,
      ],
    );

    const handleBackClick = useCallback(() => {
      const prevTab = getPreviousTab('Rules');
      if (prevTab) {
        setActiveTab(prevTab);
      }
    }, [getPreviousTab, setActiveTab]);

    const handleNextClick = useCallback(() => {
      if (
        isNextTabTokenForShopify &&
        isNextTabTokenForShopify('Rules') &&
        (mode === 'create' || mode === 'edit' || mode === 'duplicate')
      ) {
        if (!rulesValid) {
          // Show error
        } else {
          handleSave();
        }
      } else {
        const nextTab = getNextTab('Rules');
        if (nextTab) {
          if (mode === 'view') {
            handleTabChange(nextTab);
          } else {
            handleRulesValidation(nextTab);
          }
        }
      }
    }, [isNextTabTokenForShopify, mode, rulesValid, handleSave, getNextTab, handleTabChange, handleRulesValidation]);

    const nextButtonText = useMemo(
      () =>
        isNextTabTokenForShopify &&
        isNextTabTokenForShopify('Rules') &&
        (mode === 'create' || mode === 'edit' || mode === 'duplicate')
          ? getButtonText(mode)
          : 'Next',
      [isNextTabTokenForShopify, mode],
    );

    return (
      <>
        <p className="ab-test-modal__rules__title">{rulesTitle}</p>
        <div className="ab-test-modal__rulesets-container">
          <RuleEngineV2
            key={ruleEngineKey}
            initialState={ruleEngineInitialState}
            config={filteredRulesBasedOnTestTypeCatagory}
            bindings={ruleEngineBindings}
          />
          {widgetType === AbTestWidgetType.EXTENSION && (
            <p className="ab-test-modal__rules__explanation">
              *<strong>Note:</strong> The display rules configured for each extension will still apply during this test.
              This ensures that the behavior you see reflects real conditions in the live environment
            </p>
          )}
        </div>
        <div className="ab-test-modal__actions">
          <Button
            onClick={handleBackClick}
            text={'Back'}
            size={'medium'}
            variant={'secondary'}
            className="ab-test-modal__actions__cancel"
          />
          <Button onClick={handleNextClick} text={nextButtonText} size={'medium'} variant={'primary'} />
        </div>
      </>
    );
  },
);
